import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { FormProvider, useForm } from "react-hook-form";
import { TrainerFormLeft } from "./TrainerFormLeft";
import { TrainerFormRight } from "./TrainerFormRight";
import { PaziaBox } from "components/atoms";
import { useTrainerGroupsOptions } from "api/hooks";
import { TrainerApi } from "api/types";
import { TRAINER_TITLES } from "consts/trainerTitles";
import { optionalEmail, optionalPhone, optionalPostCode } from "utils/contactValidation";
import { PaziaFormButton, IDropdownOption } from "components/atoms";

interface TrainerFormProps {
  onSubmit: (data: TrainerFormValues) => Promise<void>;
  groupsOptions: IDropdownOption[];
  trainer?: TrainerApi;
}

export const TrainerForm = ({ onSubmit, trainer }: TrainerFormProps): JSX.Element | null => {
  const { options, loading } = useTrainerGroupsOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    context: { saved: trainer },
    defaultValues: {
      [TRAINER_FORM_FIELDS.TITLE]: TRAINER_TITLES.find((x) => x.label == trainer?.title)?.value,
      [TRAINER_FORM_FIELDS.FIRST_NAME]: trainer?.firstName,
      [TRAINER_FORM_FIELDS.SURNAME]: trainer?.surname,
      [TRAINER_FORM_FIELDS.PHONE]: trainer?.phone,
      [TRAINER_FORM_FIELDS.EMAIL]: trainer?.email,
      [TRAINER_FORM_FIELDS.ALTERNATIVE_EMAIL]: trainer?.alternativeEmail,
      [TRAINER_FORM_FIELDS.WEBSITE]: trainer?.website,
      [TRAINER_FORM_FIELDS.ADDRESS]: trainer?.address,
      [TRAINER_FORM_FIELDS.CITY]: trainer?.city,
      [TRAINER_FORM_FIELDS.POSTCODE]: trainer?.postCode,
      [TRAINER_FORM_FIELDS.INPOST]: trainer?.inpost,
      [TRAINER_FORM_FIELDS.DESCRIPTION]: trainer?.description ?? "",
      [TRAINER_FORM_FIELDS.SHORT_DESCRIPTION]: trainer?.shortDescription,
      [TRAINER_FORM_FIELDS.AVATAR]: trainer?.avatar,
      [TRAINER_FORM_FIELDS.PRICES]: trainer?.prices,
      [TRAINER_FORM_FIELDS.GROUPS]: (trainer?.groups ?? []).map((x) => x.id.toString()),
    },
  });
  const { handleSubmit } = methods;

  if (loading || !options) return null;
  return (
    <PaziaBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="gap-8 py-4 md:grid md:grid-cols-2">
          <TrainerFormLeft />
          <TrainerFormRight groupsOptions={options} />
          <div className="col-start-2 flex justify-end">
            <PaziaFormButton title="Zapisz" />
          </div>
        </form>
      </FormProvider>
    </PaziaBox>
  );
};

export const TRAINER_FORM_FIELDS = {
  TITLE: "title",
  FIRST_NAME: "firstName",
  SURNAME: "surname",
  PHONE: "phone",
  EMAIL: "email",
  ALTERNATIVE_EMAIL: "alternativeEmail",
  WEBSITE: "website",
  ADDRESS: "address",
  CITY: "city",
  POSTCODE: "postCode",
  INPOST: "inpost",
  PRICES: "prices",
  PRICES_NAME: "name",
  PRICES_AMOUNT: "amount",
  PRICES_TYPE: "type",
  DESCRIPTION: "description",
  SHORT_DESCRIPTION: "shortDescription",
  GROUPS: "groups",
  AVATAR: "avatar",
} as const;
const schema = yup
  .object({
    [TRAINER_FORM_FIELDS.TITLE]: yup.number().nullable(),
    [TRAINER_FORM_FIELDS.FIRST_NAME]: yup.string().required(),
    [TRAINER_FORM_FIELDS.SURNAME]: yup.string().required(),
    [TRAINER_FORM_FIELDS.GROUPS]: yup.array().of(yup.string()),
    [TRAINER_FORM_FIELDS.PHONE]: optionalPhone(),
    // The email becomes the trainer's panel account and invitation address
    [TRAINER_FORM_FIELDS.EMAIL]: optionalEmail(),
    [TRAINER_FORM_FIELDS.ALTERNATIVE_EMAIL]: optionalEmail(),
    [TRAINER_FORM_FIELDS.WEBSITE]: yup.string().nullable(),
    [TRAINER_FORM_FIELDS.ADDRESS]: yup.string().nullable(),
    [TRAINER_FORM_FIELDS.CITY]: yup.string().nullable(),
    [TRAINER_FORM_FIELDS.POSTCODE]: optionalPostCode(),
    [TRAINER_FORM_FIELDS.INPOST]: yup.string().nullable(),
    [TRAINER_FORM_FIELDS.PRICES]: yup.array().of(
      yup.object({
        [TRAINER_FORM_FIELDS.PRICES_NAME]: yup.string().required(),
        [TRAINER_FORM_FIELDS.PRICES_AMOUNT]: yup
          .number()
          .required()
          .test("not-negative", "Stawka nie może być ujemna", (v) => v == null || v >= 0),
        [TRAINER_FORM_FIELDS.PRICES_TYPE]: yup.number().required(),
      }),
    ),
    [TRAINER_FORM_FIELDS.DESCRIPTION]: yup.string().nullable(),
    [TRAINER_FORM_FIELDS.SHORT_DESCRIPTION]: yup.string().nullable(),
    [TRAINER_FORM_FIELDS.AVATAR]: yup.mixed().nullable(),
  })
  .required();
export type TrainerFormValues = yup.InferType<typeof schema>;
